import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type {
  NetWorthScenario,
  NetWorthScenarioYearlyPoint,
  ScenarioLever,
  ScenarioLeverCategory,
  ScenarioLeverConfidence,
} from '../types';
import { db } from '../lib/firebase';
import { COLLECTION_NET_WORTH_SCENARIOS, ownedItemDocId } from '../lib/firestorePaths';

const LEVER_CATEGORIES: ScenarioLeverCategory[] = [
  'investment',
  'savings',
  'debt',
  'real_estate',
  'income',
  'other',
];

const LEVER_CONFIDENCES: ScenarioLeverConfidence[] = ['high', 'medium', 'low'];

export type NetWorthScenariosState = {
  scenarios: NetWorthScenario[];
  loading: boolean;
  error: string | null;
  saveScenario: (scenario: NetWorthScenario) => Promise<void>;
};

const EMPTY_STATE: Omit<NetWorthScenariosState, 'saveScenario'> = {
  scenarios: [],
  loading: false,
  error: null,
};

function stripUndefinedFields(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined));
}

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function docItemId(d: QueryDocumentSnapshot, data: { id?: unknown }): string {
  if (typeof data.id === 'string' && data.id.trim()) return data.id.trim();
  const parts = d.id.split('__');
  return parts.length > 1 ? parts.slice(1).join('__') : d.id;
}

function normalizeLever(value: unknown, index: number): ScenarioLever | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const id = typeof row.id === 'string' && row.id.trim() ? row.id.trim() : `lever-${index}`;
  const title = typeof row.title === 'string' ? row.title.trim() : '';
  const description = typeof row.description === 'string' ? row.description : '';
  const category = LEVER_CATEGORIES.includes(row.category as ScenarioLeverCategory)
    ? (row.category as ScenarioLeverCategory)
    : 'other';
  const confidence = LEVER_CONFIDENCES.includes(row.confidence as ScenarioLeverConfidence)
    ? (row.confidence as ScenarioLeverConfidence)
    : 'medium';
  const impactEuros =
    typeof row.impactEuros === 'number' && Number.isFinite(row.impactEuros)
      ? row.impactEuros
      : undefined;

  if (!title) return null;

  return {
    id,
    title,
    description,
    category,
    confidence,
    ...(impactEuros !== undefined ? { impactEuros } : {}),
  };
}

function normalizeYearlyProjection(value: unknown): NetWorthScenarioYearlyPoint[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const row = entry as Record<string, unknown>;
      const year = asNumber(row.year);
      const netWorth = asNumber(row.netWorth);
      return { year, netWorth };
    })
    .filter((entry): entry is NetWorthScenarioYearlyPoint => entry !== null)
    .sort((a, b) => a.year - b.year);
}

function docToNetWorthScenario(d: QueryDocumentSnapshot): NetWorthScenario {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const id = docItemId(d, x);

  const levers = Array.isArray(x.levers)
    ? x.levers
        .map((entry, index) => normalizeLever(entry, index))
        .filter((entry): entry is ScenarioLever => entry !== null)
    : [];

  const assumptions = Array.isArray(x.assumptions)
    ? x.assumptions.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : [];

  return {
    id,
    userId: typeof x.userId === 'string' ? x.userId : '',
    targetNetWorth: asNumber(x.targetNetWorth),
    horizonYears: Math.max(1, Math.round(asNumber(x.horizonYears))),
    baselineNetWorth: asNumber(x.baselineNetWorth),
    projectedNetWorth: asNumber(x.projectedNetWorth),
    summary: typeof x.summary === 'string' ? x.summary : '',
    levers,
    yearlyProjection: normalizeYearlyProjection(x.yearlyProjection),
    assumptions,
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
  };
}

/** Most recently created scenarios first. */
export function sortScenariosRecentFirst(scenarios: NetWorthScenario[]): NetWorthScenario[] {
  return [...scenarios].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function useNetWorthScenarios(userId: string): NetWorthScenariosState {
  const [scenarios, setScenarios] = useState<NetWorthScenario[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setScenarios([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    const scenariosQuery = query(
      collection(db, COLLECTION_NET_WORTH_SCENARIOS),
      where('userId', '==', userId)
    );

    const applyScenarios = (docs: QueryDocumentSnapshot[]) => {
      setScenarios(sortScenariosRecentFirst(docs.map(docToNetWorthScenario)));
    };

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const snap = await getDocs(scenariosQuery);
        if (cancelled) return;

        applyScenarios(snap.docs);

        unsubscribe = onSnapshot(
          scenariosQuery,
          (next) => applyScenarios(next.docs),
          (e) => setError(e.message)
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load saved scenarios');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [userId]);

  const saveScenario = useCallback(
    async (scenario: NetWorthScenario) => {
      if (!userId) {
        throw new Error('Sign in to save a scenario');
      }
      if (!scenario.id.trim() || scenario.id.includes('__')) {
        throw new Error('Scenario id is invalid');
      }

      const payload: NetWorthScenario = {
        ...scenario,
        userId,
        createdAt: scenario.createdAt || new Date().toISOString(),
      };

      await setDoc(
        doc(db, COLLECTION_NET_WORTH_SCENARIOS, ownedItemDocId(userId, payload.id)),
        stripUndefinedFields(payload as unknown as Record<string, unknown>)
      );
    },
    [userId]
  );

  return useMemo(
    () => ({
      scenarios,
      loading,
      error,
      saveScenario,
    }),
    [scenarios, loading, error, saveScenario]
  );
}

export function emptyNetWorthScenariosState(): NetWorthScenariosState {
  return {
    ...EMPTY_STATE,
    saveScenario: async () => {
      throw new Error('Sign in to save a scenario');
    },
  };
}
